import { useState, useRef, useEffect } from 'react'
import { Send, Loader2, Bot, User, Sparkles } from 'lucide-react'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

const suggestedPrompts = [
  "Why is my computer running slow?",
  "How can I free up disk space?",
  "Check for driver updates",
  "My WiFi keeps disconnecting",
  "Run a security scan",
]

export function ChatPage() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: "Hi! I'm your PC Health Assistant powered by Claude AI. I can help diagnose computer problems, explain issues in plain English, and guide you through fixes. What can I help you with today?",
      timestamp: new Date(),
    }
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)

    // Simulate API call - will be replaced with real API
    await new Promise(resolve => setTimeout(resolve, 1500))

    // Mock AI response
    const mockResponses: Record<string, string> = {
      'slow': `Based on a quick analysis, here are the most common reasons for a slow computer:

**1. High Memory Usage** - You're currently using 58% of your RAM. While this is acceptable, having many browser tabs or applications open can push it higher.

**2. Disk Space** - Your storage is at 78% capacity. When a drive gets above 80% full, it can significantly slow down your system.

**Recommended Actions:**
• Close unused browser tabs and applications
• Run Disk Cleanup to free up space
• Consider upgrading to an SSD if you haven't already

Would you like me to run a detailed performance scan or help you free up disk space?`,
      'disk': `I can help you free up disk space! Here's what I found:

**Potential Space to Recover:**
• Temporary files: ~2.3 GB
• Windows Update cache: ~1.8 GB
• Browser cache: ~890 MB
• Recycle Bin: ~450 MB

**Total: ~5.4 GB can be safely cleaned**

Would you like me to:
1. Run Disk Cleanup automatically
2. Show you exactly what will be deleted first
3. Find large files you might want to remove manually

Just let me know which option you prefer!`,
      'default': `I understand you're asking about "${content}". Let me analyze your system to provide specific recommendations.

I'll need to run a quick diagnostic to give you accurate information. Would you like me to:

1. **Run a targeted scan** focused on this issue
2. **Check system logs** for related errors
3. **Explain common causes** and solutions

What would be most helpful?`
    }

    let response = mockResponses.default
    if (content.toLowerCase().includes('slow')) response = mockResponses.slow
    if (content.toLowerCase().includes('disk') || content.toLowerCase().includes('space')) response = mockResponses.disk

    const assistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: response,
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, assistantMessage])
    setIsLoading(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(input)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-brand-primary rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">AI Assistant</h1>
            <p className="text-sm text-slate-400">Powered by Claude</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-4 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              message.role === 'assistant' 
                ? 'bg-brand-primary' 
                : 'bg-slate-600'
            }`}>
              {message.role === 'assistant' 
                ? <Bot className="w-4 h-4 text-white" />
                : <User className="w-4 h-4 text-white" />
              }
            </div>
            <div className={`max-w-[80%] ${message.role === 'user' ? 'text-right' : ''}`}>
              <div className={`rounded-2xl px-4 py-3 ${
                message.role === 'assistant'
                  ? 'bg-slate-800 text-slate-200'
                  : 'bg-brand-primary text-white'
              }`}>
                <div className="whitespace-pre-wrap text-sm">{message.content}</div>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex gap-4">
            <div className="w-8 h-8 rounded-lg bg-brand-primary flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="bg-slate-800 rounded-2xl px-4 py-3">
              <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Prompts */}
      {messages.length === 1 && (
        <div className="px-6 pb-4">
          <p className="text-sm text-slate-400 mb-3">Suggested questions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedPrompts.map((prompt) => (
              <button
                key={prompt}
                onClick={() => sendMessage(prompt)}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-full transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-6 border-t border-slate-700/50">
        <form onSubmit={handleSubmit} className="flex gap-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Describe your computer issue..."
            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="btn-primary px-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  )
}
